import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, TrendingUp, Shield, Zap, RefreshCw } from 'lucide-react';
import { ContactForm } from '../components/ContactForm';
import { SEO } from '../components/SEO';

export const Upgrades = () => {
  const features = [
    {
      icon: <TrendingUp className="w-10 h-10 text-[#00890b]" />,
      title: 'Version Upgrades',
      description: 'Seamless migration to latest Windchill versions',
    },
    {
      icon: <Shield className="w-10 h-10 text-[#00890b]" />,
      title: 'Risk Mitigation',
      description: 'Comprehensive testing and validation processes',
    },
    {
      icon: <RefreshCw className="w-10 h-10 text-[#00890b]" />,
      title: 'Customization Migration',
      description: 'Ensure custom code compatibility with new versions',
    },
    {
      icon: <Zap className="w-10 h-10 text-[#00890b]" />,
      title: 'Performance Optimization',
      description: 'Leverage new features and performance improvements',
    },
  ];

  const upgradePhases = [
    {
      phase: 'Assessment & Planning',
      activities: [
        'Current system analysis',
        'Customization inventory',
        'Impact assessment',
        'Upgrade strategy development',
        'Resource planning',
        'Timeline definition',
      ],
    },
    {
      phase: 'Preparation',
      activities: [
        'Test environment setup',
        'Customization code review',
        'Compatibility testing',
        'Data backup procedures',
        'Rollback strategy',
        'Training material preparation',
      ],
    },
    {
      phase: 'Execution',
      activities: [
        'System upgrade execution',
        'Customization migration',
        'Data migration validation',
        'Integration testing',
        'Performance testing',
        'User acceptance testing',
      ],
    },
    {
      phase: 'Post-Upgrade',
      activities: [
        'Production cutover',
        'User training',
        'Hypercare support',
        'Performance monitoring',
        'Issue resolution',
        'Knowledge transfer',
      ],
    },
  ];

  const benefits = [
    'Access to latest Windchill features and capabilities',
    'Improved system performance and stability',
    'Enhanced security with latest patches',
    'Better integration with modern CAD systems',
    'Extended vendor support and maintenance',
    'Compliance with industry standards',
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Hire Windchill Upgrade Expert | Outsource Windchill Migration Services"
        description="Hire expert Windchill upgrade specialists. Outsource your Windchill version migration to experienced professionals. Minimize downtime with expert upgrade consultants."
        keywords="hire windchill upgrade expert, outsource windchill upgrade, hire migration specialist, windchill upgrade consultant, hire windchill migration expert"
        canonical="/hire-windchill-upgrade-expert"
      />
      <section className="bg-gradient-to-br from-white via-green-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-6 bg-[#00890b] hover:bg-[#006a08] text-white px-4 py-2">
              Upgrade Specialists
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Hire Windchill Upgrade Expert
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Outsource your Windchill upgrades to expert consultants. Hire experienced upgrade
              specialists for safe, efficient migrations with minimal business disruption
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Windchill Upgrade Expertise</h2>
          <p className="text-lg text-gray-600 mb-4">
            Upgrading Windchill is a critical project that requires careful planning, extensive
            testing, and deep technical expertise. Our team has successfully completed numerous
            Windchill upgrades across various versions and implementations, from small deployments
            to large enterprise environments.
          </p>
          <p className="text-lg text-gray-600">
            We understand the challenges of Windchill upgrades, including customization migration,
            data validation, and user adoption. Our proven methodology ensures successful upgrades
            with minimal business disruption.
          </p>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Upgrade Windchill?</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-center mb-4">{feature.icon}</div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                  <CardDescription>{feature.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Upgrade Methodology</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {upgradePhases.map((phase, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-xl text-[#00890b]">{phase.phase}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {phase.activities.map((activity, idx) => (
                      <li key={idx} className="flex items-center space-x-2">
                        <CheckCircle className="w-5 h-5 text-[#00890b] flex-shrink-0" />
                        <span className="text-gray-700">{activity}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Upgrade Benefits</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-[#00890b] flex-shrink-0 mt-1" />
                    <p className="text-gray-700 font-medium">{benefit}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Professional Windchill Upgrade Services
          </h2>
          <p className="text-lg text-gray-600 mb-4">
            Planning a Windchill upgrade? Our upgrade specialists provide end-to-end services from
            initial assessment to post-upgrade support. We handle all aspects of the upgrade
            process:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Customization Migration
              </h3>
              <p className="text-gray-600">
                We analyze your custom code, identify compatibility issues, and migrate
                customizations to work seamlessly with the new Windchill version.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Data Integrity</h3>
              <p className="text-gray-600">
                Comprehensive data validation and testing ensure your product data remains intact
                and accessible after the upgrade.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Minimal Downtime</h3>
              <p className="text-gray-600">
                Our proven upgrade process minimizes system downtime and ensures rapid return to
                normal operations.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">User Training</h3>
              <p className="text-gray-600">
                We provide training on new features and changes to ensure your team can leverage
                the upgraded system effectively.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Outsource Your Windchill Upgrade
          </h2>
          <p className="text-lg text-gray-600 mb-4">
            Windchill upgrades require specialized knowledge and significant resources. Outsourcing
            your upgrade project to our experienced team provides:
          </p>
          <ul className="list-disc list-inside text-lg text-gray-600 space-y-2 ml-4">
            <li>Proven upgrade methodology with documented success</li>
            <li>Expert knowledge of multiple Windchill versions</li>
            <li>Reduced risk through comprehensive testing</li>
            <li>Faster project completion with dedicated resources</li>
            <li>Post-upgrade support and optimization</li>
          </ul>
        </div>
      </section>

      <ContactForm />
    </div>
  );
};